import { useState, useEffect } from 'react';
import { Link, useSearch, useLocation } from 'wouter';
import { useGetLeaderboard, getGetLeaderboardQueryKey, useGetGame, getGetGameQueryKey, useListGames, getListGamesQueryKey } from '@workspace/api-client-react';
import { Navbar } from '@/components/Navbar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Trophy, Medal, Award, Flame, Skull, X, ChevronsUpDown, Check } from 'lucide-react';
import { StreakBadges } from '@/components/StreakBadges';
import { Show } from '@clerk/react';

export default function Leaderboards() {
  // wouter's useLocation() strips the query string — useSearch() is required
  // for ?game=... links (e.g. from profile GAME RECORD rows) to apply.
  const searchString = useSearch();
  const gameSlug = new URLSearchParams(searchString).get('game') || '';
  const [, navigate] = useLocation();

  // Game picker (combobox) state + full game list for choosing a board directly.
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data: games } = useListGames(undefined, { query: { queryKey: getListGamesQueryKey() } });
  const selectedGame = games?.find((g) => g.slug === gameSlug);

  const selectGame = (slug: string) => {
    setPickerOpen(false);
    navigate(slug ? `/leaderboards?game=${encodeURIComponent(slug)}` : '/leaderboards');
  };

  // Game-scoped links land on the all-time board so podium ranks match.
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'alltime' | 'dailystreak'>(gameSlug ? 'alltime' : 'daily');

  // Mirror URL state: arriving with (or without) a game filter resets the period.
  useEffect(() => {
    setPeriod(gameSlug ? 'alltime' : 'daily');
  }, [gameSlug]);

  // Score boards can rank by total points or best single game (streak board excluded).
  const [metric, setMetric] = useState<'total' | 'best'>('total');
  const metricApplies = period !== 'dailystreak';

  const params = {
    period,
    ...(gameSlug ? { gameSlug } : {}),
    ...(metricApplies && metric === 'best' ? { metric } : {}),
  };
  // Boards must be fresh every visit (the app-wide 5-min cache would show
  // stale ranks right after a play) and keep updating while on the page.
  const { data, isLoading } = useGetLeaderboard(
    params,
    { query: { queryKey: getGetLeaderboardQueryKey(params), staleTime: 0, refetchOnMount: 'always', refetchInterval: 30000 } }
  );

  const { data: gameDetail } = useGetGame(gameSlug, {
    query: { enabled: !!gameSlug, queryKey: getGetGameQueryKey(gameSlug) },
  });
  const leaderboard = data?.entries;
  const me = data?.me;
  const meVisible = !!me && !!leaderboard?.some((e) => e.rank === me.rank && e.username === me.username);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-primary drop-shadow-[0_0_10px_rgba(0,217,255,0.8)]" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-300 drop-shadow-[0_0_10px_rgba(209,213,219,0.8)]" />;
    if (rank === 3) return <Award className="h-6 w-6 text-secondary drop-shadow-[0_0_10px_rgba(176,38,255,0.8)]" />;
    return <span className="text-xl font-black font-display text-primary/50 group-hover:text-primary transition-colors">{rank}</span>;
  };

  return (
    <div className="min-h-[100dvh] bg-background relative">
      <Navbar />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8 relative z-10 animate-slide-up">
        <div className="mb-12 text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-none border-2 border-primary mb-6 glow-primary rotate-45">
            <Skull className="h-8 w-8 text-primary -rotate-45 animate-pulse" />
          </div>
          <h1 className="mb-4 text-4xl font-black tracking-tighter sm:text-7xl font-display uppercase leading-none">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-white to-secondary drop-shadow-[0_0_15px_rgba(0,217,255,0.5)]">
              HALL OF FAME
            </span>
          </h1>
          <p className="text-xl text-muted-foreground font-mono uppercase tracking-widest mt-4">
            ELITE PLAYERS ONLY
          </p>
        </div>

        <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="rounded-none border-2 border-primary/50 bg-background font-mono text-xs uppercase tracking-widest hover:border-primary"
                data-testid="button-game-picker"
              >
                {selectedGame?.title || (gameSlug ? gameSlug.replace(/-/g, ' ') : 'ALL GAMES')}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0 rounded-none border-2 border-primary/50" align="center">
              <Command>
                <CommandInput placeholder="SEARCH GAMES..." className="font-mono text-xs uppercase tracking-widest" data-testid="input-game-picker-search" />
                <CommandList>
                  <CommandEmpty className="py-6 text-center text-xs font-mono uppercase tracking-widest text-muted-foreground">No games found</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all games"
                      onSelect={() => selectGame('')}
                      className="font-mono text-xs uppercase tracking-widest rounded-none"
                      data-testid="option-game-all"
                    >
                      <Check className={`mr-2 h-4 w-4 ${!gameSlug ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                      ALL GAMES
                    </CommandItem>
                    {games?.map((game) => (
                      <CommandItem
                        key={game.slug}
                        value={game.title}
                        onSelect={() => selectGame(game.slug)}
                        className="font-mono text-xs uppercase tracking-widest rounded-none"
                        data-testid={`option-game-${game.slug}`}
                      >
                        <Check className={`mr-2 h-4 w-4 ${gameSlug === game.slug ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                        {game.title}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {gameSlug && (
            <div className="inline-flex items-center gap-3 border-2 border-secondary/50 bg-secondary/10 px-4 py-2" data-testid="banner-game-filter">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">GAME:</span>
              <span className="font-bold font-display uppercase tracking-widest text-secondary">
                {gameDetail?.game?.title || gameSlug.replace(/-/g, ' ')}
              </span>
              <Link href="/leaderboards" data-testid="link-clear-game-filter" aria-label="Clear game filter" className="text-muted-foreground hover:text-secondary transition-colors">
                <X className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>

        <div className="mb-10 flex justify-center">
          <Tabs value={period} onValueChange={(v) => setPeriod(v as any)} className="w-full max-w-lg">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 bg-background border border-primary/30 rounded-none h-auto sm:h-14 p-1 gap-1">
              <TabsTrigger value="daily" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:glow-primary" data-testid="tab-period-daily">TODAY</TabsTrigger>
              <TabsTrigger value="weekly" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:glow-secondary" data-testid="tab-period-weekly">THIS WEEK</TabsTrigger>
              <TabsTrigger value="alltime" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-primary" data-testid="tab-period-alltime">ALL TIME</TabsTrigger>
              <TabsTrigger value="dailystreak" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:glow-secondary" data-testid="tab-period-dailystreak">DAILY STREAK</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {metricApplies && (
          <div className="-mt-4 mb-10 flex justify-center">
            <Tabs value={metric} onValueChange={(v) => setMetric(v as any)} className="w-full max-w-xs">
              <TabsList className="grid w-full grid-cols-2 bg-background border border-secondary/30 rounded-none h-auto p-1 gap-1">
                <TabsTrigger value="total" className="rounded-none text-xs font-bold uppercase tracking-widest font-mono data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground" data-testid="tab-metric-total">TOTAL POINTS</TabsTrigger>
                <TabsTrigger value="best" className="rounded-none text-xs font-bold uppercase tracking-widest font-mono data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground" data-testid="tab-metric-best">BEST SINGLE GAME</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-none bg-muted/50 border border-primary/20" />
            ))}
          </div>
        ) : leaderboard && leaderboard.length > 0 ? (
          <div className="space-y-4 stagger-children">
            {leaderboard.map((entry, idx) => (
              <Link key={entry.username} href={`/u/${entry.username}`}>
                <div
                  className={`group flex items-center gap-2 sm:gap-4 rounded-none border-2 p-3 sm:p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(0,217,255,0.3)] cursor-pointer relative overflow-hidden ${
                    entry.rank === 1
                      ? 'border-primary bg-primary/10 glow-primary scale-[1.02] z-10'
                      : entry.rank === 2
                      ? 'border-white/50 bg-white/5'
                      : entry.rank === 3
                      ? 'border-secondary/50 bg-secondary/10'
                      : 'border-card-border bg-card hover:border-primary/50'
                  }`}
                  data-testid={`leaderboard-entry-${entry.rank}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent translate-x-[-100%] group-hover:animate-slide-right pointer-events-none" />
                  
                  <div className={`flex h-10 w-10 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-none border ${entry.rank === 1 ? 'border-primary bg-background' : 'border-border bg-background'}`}>
                    {getRankIcon(entry.rank)}
                  </div>

                  <Avatar className={`h-10 w-10 sm:h-14 sm:w-14 shrink-0 rounded-none border-2 ${entry.rank === 1 ? 'border-primary' : 'border-primary/20'}`}>
                    <AvatarImage src={entry.avatarUrl || undefined} alt={entry.username} />
                    <AvatarFallback className="bg-primary/20 text-primary font-mono text-xl rounded-none font-bold">
                      {entry.username[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <div className="mb-1 flex flex-wrap items-center gap-3">
                      <h3 className={`truncate text-base sm:text-2xl font-black font-display uppercase tracking-wide sm:tracking-widest ${entry.rank === 1 ? 'text-primary text-shadow-neon-cyan' : 'group-hover:text-primary transition-colors'}`}>{entry.username}</h3>
                      {entry.level && (
                        <Badge variant="outline" className="shrink-0 rounded-none font-mono text-[10px] border-primary/50 text-primary uppercase tracking-widest">
                          LVL {entry.level}
                        </Badge>
                      )}
                      <StreakBadges milestones={entry.streakBadges} />
                    </div>
                  </div>

                  <div className="shrink-0 text-right pr-0 sm:pr-4">
                    <p className={`text-xl sm:text-3xl font-black font-display tracking-tighter ${entry.rank === 1 ? 'text-primary text-shadow-neon-cyan' : 'text-foreground'}`}>
                      {entry.value.toLocaleString()}
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">SCORE</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-none border-2 border-dashed border-primary/30 bg-primary/5 p-16 text-center">
            <Trophy className="mx-auto mb-6 h-16 w-16 text-primary animate-pulse opacity-50" />
            <p className="text-xl font-mono text-primary mb-2 uppercase tracking-widest">
              NOTHING HERE YET
            </p>
            <p className="mt-1 text-sm font-mono text-muted-foreground uppercase tracking-widest">
              NO RECORDS FOUND FOR THIS PERIOD
            </p>
          </div>
        )}

        {!isLoading && me && !meVisible && (
          <div
            className="mt-8 flex items-center gap-2 sm:gap-4 rounded-none border-2 border-primary bg-primary/10 p-3 sm:p-4 glow-primary sticky bottom-4 z-20"
            data-testid="row-my-rank"
          >
            <div className="flex h-10 w-10 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-none border border-primary bg-background">
              <span className="text-lg sm:text-xl font-black font-display text-primary">#{me.rank}</span>
            </div>
            <Avatar className="h-10 w-10 sm:h-14 sm:w-14 shrink-0 rounded-none border-2 border-primary">
              <AvatarImage src={me.avatarUrl || undefined} alt={me.username} />
              <AvatarFallback className="bg-primary/20 text-primary font-mono text-xl rounded-none font-bold">
                {me.username[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-widest text-primary">YOUR RANK</p>
              <h3 className="truncate text-base sm:text-2xl font-black font-display uppercase tracking-wide text-primary">
                {me.username}
              </h3>
            </div>
            <div className="shrink-0 text-right pr-0 sm:pr-4">
              <p className="text-xl sm:text-3xl font-black font-display tracking-tighter text-primary">
                {me.value.toLocaleString()}
              </p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">SCORE</p>
            </div>
          </div>
        )}

        {!isLoading && (
          <Show when="signed-out">
            <div className="mt-8 rounded-none border border-primary/30 bg-primary/5 p-4 text-center" data-testid="nudge-sign-in-rank">
              <p className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
                <span className="text-primary">SIGN IN</span> TO SEE YOUR RANK
              </p>
            </div>
          </Show>
        )}
      </div>
    </div>
  );
}
